/** @format */

import React, { Children, Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from 'bytes-formatter';
import { Box } from '@material-ui/core';
import { AddIcon, DeleteIcon } from '../icons';
import clsx from 'clsx';
import { useSelector } from 'src/store';
import { getFileBase64 } from 'src/utils/helper';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_TEXT, MAX_NUMBER_OF_FILES } from 'src/constants';
import { Button } from '../button';
import { filesize } from 'filesize';

const UploadField = ({
  error,
  dataValue,
  accept,
  type,
  filesLimit,
  postData,
  setPostData,
  fileKey,
  setValidations
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const { updateTicketLoading, error: errorTicket } = useSelector(state => state.tickets);

  const filesValidation = useCallback(async fileArr => {
    let _validations = { attachments: null };
    if (fileArr.length > MAX_NUMBER_OF_FILES) {
      _validations = {
        attachments: t('personalData.profile_errors.maxFiles', { limit: MAX_NUMBER_OF_FILES })
      };
    } else {
      const totalSize = fileArr.reduce((acc, file) => acc + file.size, 0);
      const totalSizeMB = totalSize / (1024 * 1024);
      if (totalSizeMB > MAX_FILE_SIZE) {
        const sizePretty = filesize(totalSize);
        _validations = {
          attachments: t('personalData.profile_errors.maxSize', {
            fileSize: sizePretty,
            limit: `${MAX_FILE_SIZE_TEXT} MB`
          })
        };
      }
    }
    return _validations;
  }, []);

  const handleSelectFiles = async e => {
    const { target } = e;
    const _files = files.concat(Array.from(target.files));
    setFiles(_files);
    if (target.files && target.files.length > 0) {
      const fileArr = postData.attachments ? [...postData.attachments] : [];
      const fileNameArr = postData.attachmentFileNames ? [...postData.attachmentFileNames] : [];
      await Promise.all(
        Array.from(target.files).map(async file => {
          const fileContent = await getFileBase64(file);
          fileNameArr.push(file.name);
          fileArr.push(fileContent);
        })
      );
      const _postData = { ...postData, attachmentFileNames: fileNameArr, attachments: fileArr };
      setPostData(_postData);
      const _filesValidation = await filesValidation(_files);
      setValidations(validations => ({ ...validations, ..._filesValidation }));
    }
  };

  const removeFile = async index => {
    files.splice(index, 1);
    const _files = [...files];
    setFiles(_files);
    const fileArr = [];
    const fileNameArr = [];
    await Promise.all(
      Array.from(files).map(async file => {
        const fileContent = await getFileBase64(file);
        fileNameArr.push(file.name);
        fileArr.push(fileContent);
      })
    );
    const _postData = { ...postData, attachmentFileNames: fileNameArr, attachments: fileArr };
    setPostData(_postData);
    const _filesValidation = await filesValidation(_files);
    setValidations(validations => ({ ...validations, ..._filesValidation }));
  };

  const NoFileSelected = () => {
    return (
      <Box
        style={{
          minHeight: '156px',
          background: 'rgba(0, 0, 0, 0.05)',
          borderRadius: '8px',
          color: 'rgba(0, 0, 0, 0.4)',
          padding: '35px',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <input
          type="file"
          style={{
            zIndex: '1',
            left: '0',
            height: '100%',
            position: 'absolute',
            width: '100%',
            top: '0',
            bottom: '0',
            right: '0',
            opacity: '0',
            cursor: 'pointer'
          }}
          multiple={filesLimit > 1}
          name="documents"
          accept={accept}
          onChange={handleSelectFiles}
          key={fileKey}
        />
        <div
          style={{
            fontSize: '17px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <AddIcon color="rgba(0,0,0,0.4)" width="18.05" height="18.05" />
          <Box sx={{ mt: '22px', color: 'rgba(0, 0, 0, 0.4)', fontSize: '17px' }}>
            {type === 'video'
              ? t('Tickets.video_file_placeHolder_next')
              : type === 'document'
                ? t('Tickets.document_file_placeHolder_next')
                : type === 'screenshot'
                  ? t('Tickets.screenshot_files_placeHolder_next')
                  : t('Tickets.file_placeHolder_next')}
          </Box>
          <Box component="span" sx={{ mt: '22px', color: 'rgba(0, 0, 0, 0.4)', fontSize: '14px' }}>
            {type === 'video'
              ? t('Tickets.file_Video_Type', { fileSize: MAX_FILE_SIZE_TEXT })
              : type === 'document'
                ? t('Tickets.file_Document_Type', { fileSize: MAX_FILE_SIZE_TEXT })
                : type === 'screenshot'
                  ? t('Tickets.file_screenshot_Type', { fileSize: MAX_FILE_SIZE_TEXT })
                  : t('Tickets.file_Type', { fileSize: MAX_FILE_SIZE_TEXT })}
          </Box>
        </div>
      </Box>
    );
  };

  const FileSelected = () => {
    return (
      <>
        {files.length
          ? Children.toArray(
              files.map((file, key) => {
                const { size, name, title, id } = file;
                return (
                  <Box
                    style={{
                      minHeight: '60px',
                      background: 'rgba(0, 0, 0, 0.05)',
                      borderRadius: '8px',
                      padding: '10px 5px 10px 20px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    className={size > 134217728 ? `errBorder` : ``}
                  >
                    <Box
                      style={{
                        fontSize: '17px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start'
                      }}
                    >
                      <Box style={{ fontSize: '18px', lineHeight: '29px', color: '#000000' }}>
                        {title ? title : name}
                      </Box>
                      <Box
                        style={{
                          fontSize: '13px',
                          lineHeight: '18px',
                          letterSpacing: '0.01em',
                          color: 'rgba(0, 0, 0, 0.4)'
                        }}
                        className={size > 134217728 ? `errColor` : ``}
                      >
                        {formatBytes(size)}
                      </Box>
                    </Box>
                    <Box className="action-menu">
                      <Button noArrow ghost onClick={() => removeFile(key)}>
                        <DeleteIcon />
                      </Button>
                    </Box>
                  </Box>
                );
              })
            )
          : null}

        {dataValue && dataValue.length < filesLimit ? (
          <Box
            className="addFiles"
            style={{
              minHeight: '156px',
              background: 'rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
              color: 'rgba(0, 0, 0, 0.4)',
              padding: '35px',
              position: 'relative',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <input
                style={{
                  zIndex: '1',
                  left: '0',
                  height: '100%',
                  position: 'absolute',
                  width: '100%',
                  top: '0',
                  bottom: '0',
                  right: '0',
                  opacity: '0',
                  cursor: 'pointer'
                }}
                type="file"
                multiple
                name="documents"
                accept={accept}
                onChange={handleSelectFiles}
                key={fileKey}
              />
              <AddIcon color="rgba(0, 0, 0, 0.4)" width="18.05" height="18.05" />
              <Box component="span" sx={{ mt: '22px', color: 'rgba(0, 0, 0, 0.4)', fontSize: '17px' }}>
                {type === 'video'
                  ? t('Tickets.video_file_placeHolder_next')
                  : type === 'document'
                    ? t('Tickets.document_file_placeHolder_next')
                    : type === 'screenshot'
                      ? t('Tickets.screenshot_files_placeHolder_next')
                      : t('Tickets.file_placeHolder_next')}
              </Box>
              <Box component="span" sx={{ mt: '22px', color: 'rgba(0, 0, 0, 0.4)', fontSize: '14px' }}>
                {type === 'video'
                  ? t('Tickets.file_Video_Type', { fileSize: MAX_FILE_SIZE_TEXT })
                  : type === 'document'
                    ? t('Tickets.file_Document_Type', { fileSize: MAX_FILE_SIZE_TEXT })
                    : type === 'screenshot'
                      ? t('Tickets.file_screenshot_Type', { fileSize: MAX_FILE_SIZE_TEXT })
                      : t('Tickets.file_Type', { fileSize: MAX_FILE_SIZE_TEXT })}
              </Box>
            </Box>
          </Box>
        ) : null}

        {error && <Box className="page_error">{error}</Box>}
      </>
    );
  };

  return <div>{dataValue && dataValue.length ? <FileSelected /> : <NoFileSelected />}</div>;
};

export default UploadField;
